import React, { useState } from 'react';
import { CheckCircle2Icon, MailIcon, ClockIcon, LanguagesIcon } from 'lucide-react';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Button } from '../components/ui/Button';
import { useLang } from '../lib/i18n/LanguageContext';
import { dictionary } from '../lib/i18n/dictionary';
import { useScheduleCall } from '../components/ScheduleCallModal';
const empty = {
  name: '',
  email: '',
  company: '',
  role: '',
  message: ''
};
export function ContactPage() {
  const {
    lang
  } = useLang();
  const t = dictionary.contact;
  const c = dictionary.common;
  const [form, setForm] = useState(empty);
  const [submitted, setSubmitted] = useState(false);
  const {
    open: openSchedule
  } = useScheduleCall();
  const update = (k: keyof typeof empty, v: string) => setForm(p => ({
    ...p,
    [k]: v
  }));
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Connect to Supabase / Airtable / GoHighLevel / HubSpot — POST payload here
    console.log('[Contact] payload', form);
    setSubmitted(true);
  };
  const fieldCls = 'w-full h-11 px-3.5 bg-white border border-slate-200 rounded-sm text-[14px] focus:outline-none focus:border-navy-deep transition-colors';
  const labelCls = 'block text-[13px] font-medium text-navy-deep mb-1.5';
  return <main className="pt-32 pb-24 bg-white min-h-screen" data-id="element-707">
      <div className="max-w-6xl mx-auto px-5 md:px-8" data-id="element-708">
        <SectionHeading eyebrow={t.eyebrow[lang]} title={t.title[lang]} subtitle={t.subtitle[lang]} data-id="element-709" />

        <div className="mt-12 grid lg:grid-cols-3 gap-8" data-id="element-710">
          {/* Form */}
          <div className="lg:col-span-2 border border-slate-200 rounded-md p-6 md:p-9 bg-slate-50/40" data-id="element-711">
            {submitted ? <div className="text-center py-12" data-id="element-712">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-emerald-50 border border-emerald-200 rounded-full mb-5" data-id="element-713">
                  <CheckCircle2Icon className="w-6 h-6 text-emerald-600" data-id="element-714" />
                </div>
                <h3 className="font-serif text-2xl text-navy-deep mb-3" data-id="element-715">
                  {t.successTitle[lang]}
                </h3>
                <p className="text-slate-600 max-w-xl mx-auto leading-relaxed" data-id="element-716">
                  {t.successBody[lang]}
                </p>
              </div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-717">
                <div className="grid sm:grid-cols-2 gap-4" data-id="element-718">
                  <div data-id="element-719">
                    <label className={labelCls} data-id="element-720">{t.nameLabel[lang]} *</label>
                    <input required className={fieldCls} value={form.name} onChange={e => update('name', e.target.value)} data-id="element-721" />
                  </div>
                  <div data-id="element-722">
                    <label className={labelCls} data-id="element-723">{t.emailLabel[lang]} *</label>
                    <input required type="email" className={fieldCls} value={form.email} onChange={e => update('email', e.target.value)} data-id="element-724" />
                  </div>
                  <div data-id="element-725">
                    <label className={labelCls} data-id="element-726">{t.companyLabel[lang]}</label>
                    <input className={fieldCls} value={form.company} onChange={e => update('company', e.target.value)} data-id="element-727" />
                  </div>
                  <div data-id="element-728">
                    <label className={labelCls} data-id="element-729">{t.roleLabel[lang]}</label>
                    <select className={fieldCls} value={form.role} onChange={e => update('role', e.target.value)} data-id="element-730">
                      <option value="" data-id="element-731">{c.select[lang]}</option>
                      {t.roleOptions[lang].map(r => <option key={r} value={r} data-id="element-732">
                          {r}
                        </option>)}
                    </select>
                  </div>
                </div>
                <div data-id="element-733">
                  <label className={labelCls} data-id="element-734">{t.messageLabel[lang]} *</label>
                  <textarea required rows={5} className="w-full px-3.5 py-3 bg-white border border-slate-200 rounded-sm text-[14px] focus:outline-none focus:border-navy-deep resize-none" value={form.message} onChange={e => update('message', e.target.value)} data-id="element-735" />
                </div>
                <Button type="submit" variant="gold" size="lg" className="w-full" data-id="element-736">
                  {t.submit[lang]}
                </Button>
              </form>}
          </div>

          {/* Sidebar */}
          <aside className="space-y-4" data-id="element-737">
            <div className="border border-slate-200 rounded-md p-6 bg-white" data-id="element-738">
              <p className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-gold-deep mb-2" data-id="element-739">
                {t.directLine[lang]}
              </p>
              <a href="mailto:contact@srnavarro.example" className="inline-flex items-center gap-2 text-navy-deep hover:text-petroleum transition-colors" data-id="element-740">
                <MailIcon className="w-4 h-4" data-id="element-741" />
                contact@srnavarro.example
              </a>
            </div>

            <div className="border border-slate-200 rounded-md p-6 bg-white" data-id="element-742">
              <p className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-gold-deep mb-2" data-id="element-743">
                {t.languages[lang]}
              </p>
              <p className="inline-flex items-center gap-2 text-navy-deep" data-id="element-744">
                <LanguagesIcon className="w-4 h-4" data-id="element-745" /> English · Español
              </p>
            </div>

            <div className="border border-slate-200 rounded-md p-6 bg-white" data-id="element-746">
              <p className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-gold-deep mb-2" data-id="element-747">
                {t.hours[lang]}
              </p>
              <p className="inline-flex items-center gap-2 text-navy-deep" data-id="element-748">
                <ClockIcon className="w-4 h-4" data-id="element-749" /> {t.hoursValue[lang]}
              </p>
            </div>

            <div className="border border-gold/30 rounded-md p-6 bg-gold/[0.04]" data-id="element-750">
              <p className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-gold-deep mb-3" data-id="element-751">
                {c.scheduleCall[lang]}
              </p>
              <Button variant="primary" className="w-full !bg-navy-deep !text-white !border-navy-deep hover:!bg-navy-800" onClick={openSchedule} data-id="element-752">
                {c.scheduleCall[lang]}
              </Button>
            </div>
          </aside>
        </div>
      </div>
    </main>;
}